import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import type { Episode, Video } from "@/types/video";
import { formatViews, getRelated, getVideoById, typeToKey } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Player from "./components/Player";
import EpisodeSelector from "./components/EpisodeSelector";
import RelatedList from "./components/RelatedList";

export default function Detail() {
  const { id } = useParams();
  const list = videos as Video[];
  const video = getVideoById(list, Number(id));
  const [activeEpisode, setActiveEpisode] = useState<Episode | null>(null);
  const [shareToast, setShareToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();

  const episode = activeEpisode || (video && video.episodes.length > 0 ? video.episodes[0] : null);

  usePageMeta(
    video
      ? `${video.title} - 91视频在线观看高清完整版播放`
      : "影视详情 - 91视频在线观看",
    video
      ? `${video.title}（${video.year}·${video.region}）${video.synopsis.slice(0, 70)}…`
      : "91视频在线观看影视详情页",
    video ? `${video.title},${video.title}在线观看,${video.type},${video.tags.join(",")}` : "影视详情",
  );

  const related = useMemo(() => (video ? getRelated(list, video, 6) : []), [list, video]);

  if (!video) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-32 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </div>
        <p className="mt-5 text-lg font-medium text-foreground-900">未找到该影视资源</p>
        <p className="mt-2 text-sm text-foreground-500">资源可能已下线，去看看其他精彩内容吧</p>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareToast("链接已复制，快分享给好友吧");
    } catch {
      setShareToast("复制失败，请手动复制地址栏链接");
    }
    window.setTimeout(() => setShareToast(""), 2000);
  };

  const infoItems = [
    { label: "类型", value: video.type },
    { label: "年代", value: `${video.year} 年` },
    { label: "地区", value: video.region },
    { label: "导演", value: video.director },
    { label: "主演", value: video.cast },
    { label: "更新", value: video.updatedAt },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${typeToKey(video.type)}`} className="transition hover:text-primary-400">
          {video.type}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{video.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
          <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
            <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
              <img
                src={video.cover}
                alt={`${video.title} 封面`}
                title={`${video.title} 高清在线观看`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-1 text-xs font-medium text-background-50">
                {video.type}
              </span>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(video.id)}
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition ${
                favorite
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-200 bg-background-100 text-foreground-800 hover:border-primary-300 hover:text-primary-400"
              }`}
            >
              <i className={favorite ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-800 transition hover:border-primary-300 hover:text-primary-400"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-foreground-500">
            收藏仅保存在本设备浏览器，不同步账号
          </p>
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {video.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
            <span className="flex items-center gap-1 font-medium text-accent-500">
              <i className="ri-star-fill"></i>
              {video.rating.toFixed(1)}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {formatViews(video.views)} 次观看
            </span>
            <span className="text-foreground-300">|</span>
            <span>{video.updatedAt} 更新</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-secondary-900 transition hover:bg-secondary-200"
              >
                {tag}
              </Link>
            ))}
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200/70 bg-background-100 p-5 sm:grid-cols-2">
            {infoItems.map((item) => (
              <div key={item.label} className="flex items-start gap-3 text-sm">
                <dt className="w-12 flex-none text-foreground-500">{item.label}</dt>
                <dd className="text-foreground-900">{item.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <h2 className="mb-2 font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="text-sm leading-7 text-foreground-700">{video.synopsis}</p>
          </div>
        </div>
      </div>

      <section className="mt-10">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-5 w-1 rounded-full bg-primary-500"></span>
          <h2 className="font-heading text-lg font-bold text-foreground-950">在线播放演示</h2>
        </div>
        <Player
          key={`${video.id}-${episode ? episode.id : "main"}`}
          title={video.title}
          poster={video.banner || video.cover}
          subtitle={episode ? episode.title : "正片演示"}
        />
        <div className="mt-4">
          <EpisodeSelector
            episodes={video.episodes}
            activeId={episode ? episode.id : -1}
            onSelect={(ep) => setActiveEpisode(ep)}
          />
        </div>
      </section>

      <RelatedList items={related} />

      {shareToast && (
        <div className="pointer-events-none fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-background-200 px-4 py-2 text-sm text-foreground-900">
          {shareToast}
        </div>
      )}
    </div>
  );
}